import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Ban } from 'lucide-react'
import { Button } from '../ui/button'
import { addToIgnoreList, AlertType } from '@/lib/api'

interface IgnoreListButtonProps {
    symbol: string
    alertType: AlertType
    variant?: 'default' | 'outline' | 'ghost' | 'secondary' | 'destructive'
    size?: 'default' | 'sm' | 'lg' | 'icon'
    className?: string
    showLabel?: boolean
    onSuccess?: () => void
}

/**
 * Button to add a symbol to the global ignore list for a specific alert type.
 * This permanently ignores the symbol until manually removed.
 */
export function IgnoreListButton({
    symbol,
    alertType,
    variant = 'outline',
    size = 'sm',
    className,
    showLabel = true,
    onSuccess,
}: IgnoreListButtonProps) {
    const queryClient = useQueryClient()

    const alertTypeLabels: Record<AlertType, string> = {
        oi: 'OI',
        orderbook: 'Orderbook',
        mark_price: 'Mark Price',
        price_diff: 'Price Diff',
        funding: 'Funding',
        splash: 'Splash',
        spread: 'Spread',
    }

    const mutation = useMutation({
        mutationFn: () => addToIgnoreList(alertType, symbol),
        onSuccess: (data) => {
            if (data.ok) {
                // Invalidate relevant queries to refresh UI
                queryClient.invalidateQueries({ queryKey: ['settings', 'global'] })
                queryClient.invalidateQueries({ queryKey: ['alerts', 'oi'] })
                queryClient.invalidateQueries({ queryKey: ['alerts', 'orderbook'] })
                queryClient.invalidateQueries({ queryKey: ['alerts', 'markprice'] })
                queryClient.invalidateQueries({ queryKey: ['alerts', 'pricediff'] })
                queryClient.invalidateQueries({ queryKey: ['alerts', 'funding'] })
                onSuccess?.()
            }
        },
    })

    return (
        <Button
            variant={variant}
            size={size}
            className={className}
            onClick={(e) => {
                e.stopPropagation()
                mutation.mutate()
            }}
            disabled={mutation.isPending}
            title={`Add ${symbol} to ${alertTypeLabels[alertType]} ignore list`}
        >
            <Ban className="h-3 w-3" />
            {showLabel && (
                <span className="ml-1">{mutation.isPending ? 'Adding...' : 'Ignore'}</span>
            )}
        </Button>
    )
}
